import React, { useContext, useState, createContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import SettingsIcon from '@mui/icons-material/Settings';
import RoomIcon from '@mui/icons-material/Room';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, set } from 'lodash';
import { toast } from 'react-toastify';

// Apis
import { quoteApi } from '@/main/api';

// Screens
import StepOneScreen from './steps/stepOne/stepOneScreen';
import StepTwoScreen from './steps/stepTwo/stepTwoScreen';
import StepThreeScreen from './steps/stepThree/stepThreeScreen';

// Utils & Enum
import { getTranslateByKey, haveIgnoreElement } from '@/main/services/utils';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { OrderStepsEnum } from '@/main/services/enum/OrderStepsEnum';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';

// Redux
import { loadCart, saveCart } from '@/main/store/cart';
import { GetQuoteItemResponse, QuoteGetItemConditionsWrapper, QuoteResponse } from '@/main/models/response';
import { destructGetItemsResponse, transformCartItemsToCart } from '@/main/store/cart/cartUtils';

interface ICreateOrderProvider {
  children: React.ReactNode;
}
interface Step {
  id: number;
  title: string;
  icon: React.ComponentType;
  component: React.ComponentType;
}
interface ICreateOrderContext {
  currentStep: number;
  steps: Step[];
  resultQuoteDetail: QuoteResponse | null;
  openModalCancel: boolean;
  isLoadingInfoOrder: boolean;
  setOpenModalCancel: (value: boolean) => void;
  displayModalCancel: () => void;
  openModalReQuote: boolean;
  setOpenModalReQuote: (value: boolean) => void;
  displayModalReQuote: () => void;
  setCurrentStep: (value: number) => void;
  handleStepClick: (stepId: number) => void;
  handleCompletedStep: () => void;
  handlePreviousStep: () => void;
  handleSaveStepData: (idStep: number, isCompleted: boolean, data: any) => void;
  updateGuidQuote: (guidQuote: string) => void;
  guidQuote: string | null;
  savedData: IStepsData[];
  showSupplierParts: boolean;
  setShowSupplierParts: (value: boolean) => void;
  displaySupplierParts: (
    value: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  isCompareSupplier: boolean;
  displayCompareSuppliers: (
    value: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  partGuid: string;
  integration: boolean;
  isQuoteOpen: boolean;
  isQuoteManual: boolean;
  isQuoteCanceled: boolean;
  isWaitingRelease: boolean;
  setPartGuid: (value: string) => void;
  setResetSteps: (value: boolean) => void;
  setIsCompareSupplier: (value: boolean) => void;
  setIsSeeSuppliersAvailable: (value: boolean) => void;
  isSeeSuppliersAvailable: boolean;
  displaySeeSuppliersAvailable: (
    idPart: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  externalPartId: string | null;
  dataQuoteItem: GetQuoteItemResponse | null;
  setDataQuoteItem: React.Dispatch<React.SetStateAction<GetQuoteItemResponse | null>>;
  setExternalPartId: (value: string | null) => void;
  loadInfoOrder: (guidQuote: string | null) => void;
  handleContinueShopping: () => void;
  setIsQuoteCanceled: (value: boolean) => void;
  setIsQuoteOpen: (value: boolean) => void;
  setIsQuoteManual: (value: boolean) => void;
  setIsWaitingRelease: (value: boolean) => void;
  setIntegration: (value: boolean) => void;
  refreshQuoteByGetItems: (result: QuoteGetItemConditionsWrapper) => { isCanceledQuote: boolean };
  setIsUploadPaint: React.Dispatch<React.SetStateAction<boolean>>;
  isUploadPaint: boolean;
  setIsPaymentPending: React.Dispatch<React.SetStateAction<boolean>>;
  isPaymentPending: boolean;
  setIsQuoteBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  isQuoteBlocked: boolean;
  clientSecret: string;
  setClientSecret: React.Dispatch<React.SetStateAction<string>>;
  guidCountry: string;
  setGuidCountry: React.Dispatch<React.SetStateAction<string>>;
  guidCompanyBuyer: string;
  setGuidCompanyBuyer: React.Dispatch<React.SetStateAction<string>>;
  guidVehicleManufacturer: string;
  setGuidVehicleManufacturer: React.Dispatch<React.SetStateAction<string>>;
  hasOrderByClaim: boolean;
  setHasOrderByClaim: React.Dispatch<React.SetStateAction<boolean>>;
  idProcurementStatus: number | null;
  setIdProcurementStatus: React.Dispatch<React.SetStateAction<number | null>>;
}
interface IStepsData {
  idStep: number;
  isCompleted?: boolean;
  data: any;
}

const CreateOrderContext = createContext<ICreateOrderContext>({
  currentStep: 0,
  openModalCancel: false,
  resultQuoteDetail: null,
  steps: [],
  isLoadingInfoOrder: false,
  dataQuoteItem: null,
  setDataQuoteItem: () => { },
  setOpenModalCancel: () => { },
  displayModalCancel: () => { },
  openModalReQuote: false,
  setOpenModalReQuote: () => { },
  displayModalReQuote: () => { },
  setCurrentStep: () => { },
  handleStepClick: () => { },
  handleCompletedStep: () => { },
  handlePreviousStep: () => { },
  updateGuidQuote: (guidQuote: string) => { },
  handleSaveStepData: (idStep: number, isCompleted: boolean, data: []) => { },
  setIsUploadPaint: () => { },
  isUploadPaint: false,
  guidQuote: '',
  savedData: [],
  isQuoteOpen: false,
  isQuoteManual: false,
  isQuoteCanceled: false,
  isWaitingRelease: false,
  showSupplierParts: false,
  setShowSupplierParts: () => { },
  displaySupplierParts: () => { },
  displayCompareSuppliers: () => { },
  isCompareSupplier: false,
  partGuid: '',
  integration: false,
  setPartGuid: () => { },
  setResetSteps: () => { },
  setIsCompareSupplier: () => { },
  setIsSeeSuppliersAvailable: () => { },
  isSeeSuppliersAvailable: false,
  displaySeeSuppliersAvailable: () => { },
  setExternalPartId: () => { },
  externalPartId: null,
  loadInfoOrder: (guidQuote: string | null) => { },
  handleContinueShopping: () => { },
  setIsQuoteCanceled: () => { },
  setIsQuoteOpen: () => { },
  setIsQuoteManual: () => { },
  setIsWaitingRelease: () => { },
  setIntegration: () => { },
  isQuoteBlocked: false,
  setIsQuoteBlocked: () => { },
  refreshQuoteByGetItems: () => ({ isCanceledQuote: false }),
  clientSecret: '',
  setClientSecret: () => { },
  isPaymentPending: false,
  setIsPaymentPending: () => { },
  guidCountry: '',
  setGuidCountry: () => { },
  guidCompanyBuyer: '',
  setGuidCompanyBuyer: () => { },
  guidVehicleManufacturer: '',
  setGuidVehicleManufacturer: () => { },
  hasOrderByClaim: false,
  setHasOrderByClaim: () => { },
  idProcurementStatus: null,
  setIdProcurementStatus: () => { }
});

const INITIAL_STEP_STATE: IStepsData[] = [
  {
    idStep: OrderStepsEnum.VehicleInformation,
    isCompleted: false,
    data: null
  },
  {
    idStep: OrderStepsEnum.Parts,
    isCompleted: false,
    data: null
  },
  {
    idStep: OrderStepsEnum.Summary,
    isCompleted: false,
    data: null
  }
];

function CreateOrderProvider({ children }: ICreateOrderProvider) {
  const steps = [
    {
      id: OrderStepsEnum.VehicleInformation,
      icon: DriveEtaIcon,
      component: StepOneScreen
    },
    {
      id: OrderStepsEnum.Parts,
      icon: SettingsIcon,
      component: StepTwoScreen
    },
    {
      id: OrderStepsEnum.Summary,
      icon: RoomIcon,
      component: StepThreeScreen
    }
  ];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepSearchParam = searchParams.get('step');
  const INITIAL_STEP = stepSearchParam
    ? Number(stepSearchParam)
    : OrderStepsEnum.VehicleInformation;
  const guidQuote = searchParams.get('guidQuote');
  const guidPart = searchParams.get('guidPart');
  const urlExternalPartId = searchParams.get('externalPartId');

  const [savedData, setSavedData] = useState<IStepsData[]>(INITIAL_STEP_STATE);
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const [openModalCancel, setOpenModalCancel] = useState<boolean>(false);
  const [openModalReQuote, setOpenModalReQuote] = useState<boolean>(false);
  const [isLoadingInfoOrder, setIsLoadingInfoOrder] = useState<boolean>(false);
  const [showSupplierParts, setShowSupplierParts] = useState<boolean>(false);
  const [isCompareSupplier, setIsCompareSupplier] = useState<boolean>(false);
  const [isQuoteBlocked, setIsQuoteBlocked] = useState<boolean>(false);
  const [isSeeSuppliersAvailable, setIsSeeSuppliersAvailable] = useState<
    boolean
  >(false);
  const [partGuid, setPartGuid] = useState<string>('');
  const [integration, setIntegration] = useState<boolean>(false);
  const [resetSteps, setResetSteps] = useState<boolean>(false);
  const [isQuoteCanceled, setIsQuoteCanceled] = useState<boolean>(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState<boolean>(false);
  const [idProcurementStatus, setIdProcurementStatus] = useState<number | null>(null);
  const [isQuoteManual, setIsQuoteManual] = useState<boolean>(false);
  const [isWaitingRelease, setIsWaitingRelease] = useState<boolean>(false);
  const [isPaymentPending, setIsPaymentPending] = useState<boolean>(false);
  const [externalPartId, setExternalPartId] = useState<string | null>(null);
  const [guidCountry, setGuidCountry] = useState<string>('');
  const [guidCompanyBuyer, setGuidCompanyBuyer] = useState<string>('');
  const [guidVehicleManufacturer, setGuidVehicleManufacturer] = useState<string>('');
  const [resultQuoteDetail, setResultQuoteDetail] = useState<QuoteResponse | null>(null);
  const [
    dataQuoteItem,
    setDataQuoteItem
  ] = useState<GetQuoteItemResponse | null>(null);
  const [isUploadPaint, setIsUploadPaint] = useState<boolean>(false);
  const [hasOrderByClaim, setHasOrderByClaim] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>('');

  function handleContinueShopping() {
    if (isPaymentPending) return;
    setShowSupplierParts(false);
    setIsCompareSupplier(false);
    setPartGuid('');
    setIsSeeSuppliersAvailable(false);
    setExternalPartId(null);
    const stepChanged = OrderStepsEnum.Parts;
    setCurrentStep(stepChanged);
    changeParamsUrl(String(stepChanged), null, null);
  }

  function changeParamsUrl(
    stepChanged: string,
    guidPart: string | null,
    externalPartId: string | null
  ) {
    setSearchParams({
      ...(guidQuote && { guidQuote }),
      step: stepChanged,
      ...(guidPart && { guidPart }),
      ...(externalPartId && { externalPartId })
    });
  }

  function displayCompareSuppliers(
    guidPart: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (isPaymentPending) return;

    setCurrentStep(OrderStepsEnum.Parts);
    setIsCompareSupplier(true);
    displaySupplierParts(guidPart, externalPartId, event);
  }

  function displaySupplierParts(
    value: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (haveIgnoreElement(event.target as HTMLElement)) return;
    window.scrollTo({ top: 0 });
    setShowSupplierParts(true);
    setPartGuid(value);
    setExternalPartId(externalPartId);

    changeParamsUrl(String(currentStep), value, externalPartId);
  }

  function displaySeeSuppliersAvailable(
    guidPart: string,
    externalPartId: string | null,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (haveIgnoreElement(event.target as HTMLElement)) return;
    window.scrollTo({ top: 0 });

    setCurrentStep(OrderStepsEnum.Parts);
    setIsSeeSuppliersAvailable(true);
    setShowSupplierParts(true);
    setPartGuid(guidPart);
    setExternalPartId(externalPartId);

    changeParamsUrl(String(currentStep), guidPart, externalPartId);
  }

  function handleStepClick(stepId: number) {
    if (isQuoteCanceled || isQuoteBlocked || isPaymentPending) return;

    // Notation: Reset props for Select Suppliers
    setShowSupplierParts(false);
    setIsCompareSupplier(false);
    setIsSeeSuppliersAvailable(false);
    setPartGuid('');
    setExternalPartId(null);

    changeParamsUrl(String(stepId), null, null);

    const findStep = savedData.find(data => data.idStep === stepId);
    if (OrderStepsEnum.Parts === stepId || findStep?.isCompleted) {
      setCurrentStep(stepId);
    }
  }

  function handleCompletedStep() {
    const findStep = savedData.find(data => data.idStep === currentStep);
    if (findStep) {
      handleSaveStepData(findStep.idStep, true, findStep.data);
      const stepChanged = currentStep + 1;
      setCurrentStep(stepChanged);

      changeParamsUrl(String(stepChanged), null, null);
    }
  }

  function displayModalCancel() {
    setOpenModalCancel(true);
  }

  function displayModalReQuote() {
    setOpenModalReQuote(true);
  }

  function handlePreviousStep() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  function handleSaveStepData(idStep: number, isCompleted: boolean, data: any) {
    setSavedData(prevState => {
      const newStateSteps = prevState.map(step => {
        if (step.idStep === idStep) {
          return {
            idStep: step.idStep,
            isCompleted,
            data
          };
        }
        return step;
      });

      return newStateSteps;
    });
  }

  function loadPreviousSteps() {
    setSavedData(prevState => {
      const indexCurrentStep = prevState.findIndex(
        step => step.idStep === currentStep
      );

      return prevState.map((step, index) =>
        index < indexCurrentStep
          ? {
            idStep: step.idStep,
            isCompleted: true,
            data: step.data
          }
          : step
      );
    });
  }

  function refreshQuoteByGetItems(result: QuoteGetItemConditionsWrapper) {
    const {
      dataCart,
      dataCartNoSupplierAssigned,
      dataCartUnansweredItems,
      dataCartUnansweredCanceledItems,
      dataCartUnansweredRequotedItems
    } = destructGetItemsResponse(result);

    const formattedPart = transformCartItemsToCart(
      dataCart,
      String(guidQuote)
    );
    dispatch(saveCart(
      formattedPart,
      dataCartNoSupplierAssigned,
      dataCartUnansweredItems,
      dataCartUnansweredCanceledItems,
      dataCartUnansweredRequotedItems,
      false,
      null
    ))

    const isCanceledQuote = isEmpty(dataCart) && isEmpty(dataCartNoSupplierAssigned) && isEmpty(dataCartUnansweredItems);

    if (isCanceledQuote) {
      loadInfoOrder(guidQuote);
      toast.success(getTranslateByKey('information.successfullyCanceledQuote'))
    }

    return {
      isCanceledQuote
    }
  }

  function loadInfoOrder(guidQuoteProp: string | null) {
    if (guidQuoteProp) {
      setIsLoadingInfoOrder(true);
      quoteApi
        .getQuoteById(guidQuoteProp)
        .then(response => {
          const { result } = response.data;
          setResultQuoteDetail(result);
          if (result.hasOrder) {
            navigate(
              `${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${result.orderNumber}`
            );
          } else {
            handleSaveStepData(OrderStepsEnum.VehicleInformation, true, result);
            setIntegration(result.integration);
            setGuidCountry(result.guidCountry);
            setGuidCompanyBuyer(result.guidCompanyBuyer);
            setGuidVehicleManufacturer(result.vehicleGuidManufacturer);

            const isQuoteOpen =
              result.idProcurementStatus === ProcurementStatusEnum.Open;
            setIsQuoteOpen(isQuoteOpen);

            const isQuoteManual =
              result.idProcurementStatus === ProcurementStatusEnum.Manual ||
              result.idProcurementStatus ===
              ProcurementStatusEnum.WaitingRelease;
            setIsQuoteManual(isQuoteManual);

            const isQuoteCancel =
              result.idProcurementStatus === ProcurementStatusEnum.Cancelled;
            setIsQuoteCanceled(isQuoteCancel);

            setIsQuoteBlocked(result.quoteBlocked);

            if (isQuoteCancel || result.hasQuoteItem) {
              setCurrentStep(OrderStepsEnum.Summary);

              changeParamsUrl(String(OrderStepsEnum.Summary), null, null);
            }

            setIsPaymentPending(result.paymentPending);

            const isWaitingRelease =
              result.idProcurementStatus === ProcurementStatusEnum.WaitingRelease;
            setIsWaitingRelease(isWaitingRelease);

            setHasOrderByClaim(result.hasOrderByClaim)
            setIdProcurementStatus(result.idProcurementStatus);
          }
        })
        .finally(() => {
          setIsLoadingInfoOrder(false);
        });
    }
  }

  function updateGuidQuote(guidQuote: string) {
    setSearchParams({
      ...(guidQuote && { guidQuote }),
      step: String(currentStep)
    });
  }

  useEffect(() => {
    loadPreviousSteps();
    loadInfoOrder(guidQuote);
    if (urlExternalPartId) setExternalPartId(urlExternalPartId);
  }, []);

  useEffect(() => {
    if (resetSteps) {
      setSavedData(INITIAL_STEP_STATE);
    }
  }, [resetSteps]);

  useEffect(() => {
    changeParamsUrl(String(currentStep), guidPart, externalPartId);

    if (guidQuote && currentStep > OrderStepsEnum.VehicleInformation) {
      dispatch(loadCart(guidQuote));
    }

    if (guidPart) {
      setShowSupplierParts(true);
      setPartGuid(guidPart);
    }
  }, [currentStep]);

  return (
    <CreateOrderContext.Provider
      value={{
        currentStep,
        resultQuoteDetail,
        setCurrentStep,
        steps,
        handleStepClick,
        handleCompletedStep,
        handlePreviousStep,
        handleSaveStepData,
        updateGuidQuote,
        guidQuote,
        savedData,
        displayModalCancel,
        openModalCancel,
        isLoadingInfoOrder,
        setOpenModalCancel,
        displayModalReQuote,
        openModalReQuote,
        setOpenModalReQuote,
        showSupplierParts,
        setShowSupplierParts,
        displaySupplierParts,
        displayCompareSuppliers,
        isCompareSupplier,
        partGuid,
        integration,
        isQuoteOpen,
        isQuoteManual,
        isQuoteCanceled,
        isWaitingRelease,
        isUploadPaint,
        setIsUploadPaint,
        setPartGuid,
        setResetSteps,
        setIsCompareSupplier,
        isSeeSuppliersAvailable,
        setIsSeeSuppliersAvailable,
        displaySeeSuppliersAvailable,
        setExternalPartId,
        externalPartId,
        loadInfoOrder,
        handleContinueShopping,
        setIsQuoteCanceled,
        setIsQuoteManual,
        setIsQuoteOpen,
        setIsWaitingRelease,
        setIntegration,
        refreshQuoteByGetItems,
        dataQuoteItem,
        setDataQuoteItem,
        isQuoteBlocked,
        setIsQuoteBlocked,
        clientSecret,
        setClientSecret,
        isPaymentPending,
        setIsPaymentPending,
        guidCountry,
        setGuidCountry,
        guidCompanyBuyer,
        setGuidCompanyBuyer,
        idProcurementStatus,
        setIdProcurementStatus,
        guidVehicleManufacturer,
        setGuidVehicleManufacturer,
        hasOrderByClaim,
        setHasOrderByClaim
      }}>
      {children}
    </CreateOrderContext.Provider>
  );
}

const useCreateOrderContext = () => useContext(CreateOrderContext);

export { CreateOrderProvider, useCreateOrderContext };
